import type { ImageEditCapabilities } from './ai';

export type EditImageTab =
  | 'crop'
  | 'compress'
  | 'erase'
  | 'repaint'
  | 'markup'
  | 'upscale'
  | 'split';

/** 超清 Tab 展示用尺寸/预览；提交身份走 saveTarget，不走编辑图片链路。 */
export interface EditImageUpscaleContext {
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
  width?: number | null;
  height?: number | null;
  aspectRatio?: number;
}

export type EditImageEditAction = 'crop' | 'compress' | 'markup' | 'erase' | 'repaint';
export type EditImageLocalEditAction = 'crop' | 'compress' | 'markup';

export type EditImageSaveMode =
  | 'plan-history'
  | 'new-plan'
  | 'new-footage-file'
  | 'new-markup-reference';

export const STORYBOARD_EDIT_ACTION_TRIM = 5;
export const STORYBOARD_EDIT_ACTION_COMPRESS = 6;
export const STORYBOARD_EDIT_ACTION_MARKUP = 7;
export const STORYBOARD_EDIT_ACTION_ERASE = 8;
export const STORYBOARD_EDIT_ACTION_REPAINT = 9;
export const STORYBOARD_EDIT_ACTION_SPLIT = 10;

export function resolveAiImageEditActionLabel(value: unknown): '擦除' | '重绘' | undefined {
  if (value === STORYBOARD_EDIT_ACTION_ERASE) return '擦除';
  if (value === STORYBOARD_EDIT_ACTION_REPAINT) return '重绘';
  return undefined;
}

export function resolveFootageEditAction(value: unknown): EditImageEditAction | undefined {
  if (
    value === 'crop' ||
    value === 'compress' ||
    value === 'markup' ||
    value === 'erase' ||
    value === 'repaint'
  ) {
    return value;
  }
  if (value === 'trim') return 'crop';
  return undefined;
}

/** 文件类型引用线只给标注；裁剪/压缩有溯源也不画。 */
export function isFootageMarkupLineageAction(value: unknown): boolean {
  return resolveFootageEditAction(value) === 'markup';
}

export function resolveFootageEditActionFromRecord(input: {
  editAction?: unknown;
  metadata?: Record<string, unknown> | null;
}): EditImageEditAction | undefined {
  const direct = resolveFootageEditAction(input.editAction);
  if (direct) return direct;
  const metadata = input.metadata;
  if (!metadata) return undefined;
  const editedFrom = metadata.editedFrom ?? metadata.edited_from;
  if (editedFrom && typeof editedFrom === 'object') {
    const record = editedFrom as Record<string, unknown>;
    const fromEdited = resolveFootageEditAction(record.editAction ?? record.edit_action);
    if (fromEdited) return fromEdited;
  }
  return resolveFootageEditAction(metadata.editAction ?? metadata.edit_action);
}

export function canMarkupStoryboardPlanImage(capabilities: {
  canCreatePlan: boolean;
  canUploadImage: boolean;
  canDeletePlan: boolean;
}): boolean {
  return capabilities.canCreatePlan && capabilities.canUploadImage && capabilities.canDeletePlan;
}

export function collectOccupiedEditedImageNames(
  ...nameLists: readonly (readonly string[] | undefined)[]
): string[] {
  const occupied = new Set<string>();
  for (const list of nameLists) {
    if (!list) continue;
    for (const name of list) {
      const trimmed = name.trim();
      if (trimmed) occupied.add(trimmed);
    }
  }
  return [...occupied];
}

const IMAGE_EXTENSION = String.raw`\.(?:png|jpe?g|webp|gif|bmp|avif|heic|heif|tiff?|svg|ico)`;
const IMAGE_EDIT_SUFFIX = String.raw`(?:-(?:标注|擦除|重绘|裁剪|压缩)[0-9]*|\s+-\s+超清(?:[0-9]+(?:\.[0-9]+)?x|[0-9]+)?|-[0-9]{13}|（V[0-9]+）)`;
const EDITED_IMAGE_EXTENSION_PATTERN = new RegExp(
  `${IMAGE_EXTENSION}(?=(?:${IMAGE_EXTENSION}|${IMAGE_EDIT_SUFFIX})*$)`,
  'gi'
);

/** 图片专用：保留正常多点名称、操作历史、时间戳和版本号，不用于方案名称。 */
export function normalizeEditedImageBaseName(name: string): string {
  return name.trim().replace(EDITED_IMAGE_EXTENSION_PATTERN, '');
}

const IMAGE_EXTENSION_BY_MIME_TYPE: Readonly<Record<string, string>> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
  'image/avif': 'avif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/tiff': 'tif',
  'image/svg+xml': 'svg',
  'image/x-icon': 'ico',
  'image/vnd.microsoft.icon': 'ico',
};

/** 只修改文件名，不改变图片字节；扩展名按文件 MIME 选择。 */
export function normalizeEditedImageFileName(name: string, mimeType: string): string {
  const extension = IMAGE_EXTENSION_BY_MIME_TYPE[mimeType.split(';', 1)[0].trim().toLowerCase()];
  if (!extension) return name;
  return `${normalizeEditedImageBaseName(name) || 'edited-image'}.${extension}`;
}

/** 图片派生名统一先清理再追加任意操作文案；方案名称继续使用 nextEditedImageName。 */
export function nextDerivedImageName(
  sourceName: string,
  actionLabel: string,
  existingNames: readonly string[]
): string {
  return nextEditedImageName(normalizeEditedImageBaseName(sourceName), actionLabel, existingNames);
}

export function nextEditedImageName(
  sourceName: string,
  actionLabel: string,
  existingNames: readonly string[]
): string {
  const trimmedSource = sourceName.trim();
  const base = trimmedSource ? `${trimmedSource}-${actionLabel}` : actionLabel;
  const occupied = new Set(existingNames.map((name) => name.trim()).filter(Boolean));

  if (!occupied.has(base)) {
    return base;
  }

  let suffix = 2;
  while (occupied.has(`${base}${suffix}`)) {
    suffix += 1;
  }
  return `${base}${suffix}`;
}

export type ImageEditCanvasContext =
  | { projectId: string; contextType: 'storyboard'; storyboardId: string }
  | {
      projectId: string;
      contextType: 'asset';
      assetType: 'character' | 'location' | 'prop';
      assetId: string;
    };

export type ImageEditPinOutcome =
  | { status: 'placed'; nodeId: string }
  | { status: 'not_ready' }
  | { status: 'cannot_place'; reason: string };

export interface ImageEditPinIntent {
  jobId: string;
  canvas: ImageEditCanvasContext;
  sourceNodeId: string;
  sourceResourceId: string;
  isCurrent: () => boolean;
  onNodeReserved: (nodeId: string) => void;
}

export type EditImageSaveTarget =
  | {
      kind: 'storyboard-image';
      storyboardId: string;
      planId: string;
      sourceImageId: string;
      sourceImageName?: string;
      parentProjectIdForStoryboardTags?: string;
    }
  | {
      kind: 'project-resource';
      projectId: string;
      permissionProjectId?: string;
      elementTagId: string;
      resourceType: 'character' | 'location' | 'prop';
      sourceResourceId: string;
      resourceName?: string;
      refreshElementPresentation?: boolean;
    }
  | {
      kind: 'footage-file';
      projectId: string;
      permissionProjectId?: string;
      sourceResourceId?: string;
      resourceName?: string;
      pinOnCanvas?: boolean;
      sourceNodeId?: string;
      /** 只用于前端恢复，不进入生成请求。 */
      canvasContext?: ImageEditCanvasContext;
    }
  | {
      kind: 'markup-reference';
      projectId: string;
      storyboardId: string;
      storyboardTitle?: string;
      sourceNodeId: string;
      sourceResourceId: string;
      sourceResourceType: number;
      resourceName?: string;
      sourcePlanId?: string;
      sourcePlanName?: string;
    };

export function canUseAiImageEdit(
  enableAiEdit: boolean,
  saveTarget: EditImageSaveTarget | null
): saveTarget is EditImageSaveTarget {
  if (!enableAiEdit || !saveTarget) return false;
  if (saveTarget.kind === 'footage-file') return Boolean(saveTarget.sourceResourceId);
  return true;
}

export type ImageEditRequestTarget =
  | {
      kind: 'storyboard-image';
      storyboardId: string;
      planId: string;
      sourceImageId: string;
      sourceImageName?: string;
    }
  | {
      kind: 'project-resource';
      projectId: string;
      permissionProjectId?: string;
      elementTagId: string;
      resourceType: 'character' | 'location' | 'prop';
      sourceResourceId: string;
      resourceName?: string;
    }
  | {
      kind: 'footage-file';
      projectId: string;
      permissionProjectId?: string;
      sourceResourceId: string;
      resourceName?: string;
    }
  | {
      kind: 'markup-reference';
      projectId: string;
      storyboardId: string;
      storyboardTitle?: string;
      sourceResourceId: string;
      sourceResourceType: number;
      resourceName?: string;
      sourcePlanId?: string;
      sourcePlanName?: string;
    };

/** 只把服务端落稿所需身份发到接口，画布节点和摆放意图保留在前端。 */
export function buildImageEditRequestTarget(
  target: EditImageSaveTarget
): ImageEditRequestTarget | null {
  if (target.kind === 'storyboard-image') {
    return {
      kind: target.kind,
      storyboardId: target.storyboardId,
      planId: target.planId,
      sourceImageId: target.sourceImageId,
      sourceImageName: target.sourceImageName,
    };
  }
  if (target.kind === 'project-resource') {
    return {
      kind: target.kind,
      projectId: target.projectId,
      permissionProjectId: target.permissionProjectId,
      elementTagId: target.elementTagId,
      resourceType: target.resourceType,
      sourceResourceId: target.sourceResourceId,
      resourceName: target.resourceName,
    };
  }
  if (target.kind === 'footage-file') {
    if (!target.sourceResourceId) return null;
    return {
      kind: target.kind,
      projectId: target.projectId,
      permissionProjectId: target.permissionProjectId,
      sourceResourceId: target.sourceResourceId,
      resourceName: target.resourceName,
    };
  }
  return {
    kind: target.kind,
    projectId: target.projectId,
    storyboardId: target.storyboardId,
    storyboardTitle: target.storyboardTitle,
    sourceResourceId: target.sourceResourceId,
    sourceResourceType: target.sourceResourceType,
    resourceName: target.resourceName,
    sourcePlanId: target.sourcePlanId,
    sourcePlanName: target.sourcePlanName,
  };
}

export type EditImageSaveResult =
  | {
      kind: 'storyboard-image';
      resourceId: string;
      planId?: string;
    }
  | {
      kind: 'project-resource';
      resourceId: string;
    }
  | {
      kind: 'footage-file';
      resourceId: string;
      name?: string;
      fileUrl?: string;
      thumbnailUrl?: string | null;
      mimeType?: string | null;
      width?: number | null;
      height?: number | null;
      mediaKind?: 'image' | 'video' | 'audio' | null;
      editAction?: EditImageEditAction;
    }
  | {
      kind: 'markup-reference';
      attachmentId: string;
      fileUrl: string;
      name?: string;
      mimeType?: string | null;
      width?: number | null;
      height?: number | null;
      editAction?: EditImageEditAction;
    };

export function buildMarkupPlanSelection(
  result: Extract<EditImageSaveResult, { kind: 'storyboard-image' }>
): { tagId?: string; previewItemId: string } {
  return result.planId
    ? { tagId: result.planId, previewItemId: result.resourceId }
    : { previewItemId: result.resourceId };
}

type ImageEditCapableModel = {
  capabilities?: {
    imageEdit?: ImageEditCapabilities;
  };
};

/** 从已加载的 /model-definitions 目录按 imageEdit.<operation>.supported 筛选。 */
export function filterImageModelsByEditOperation<T extends ImageEditCapableModel>(
  catalogModels: readonly T[] | undefined,
  operation: 'erase' | 'repaint' | 'gridRepaint' | null
): T[] {
  if (!catalogModels || catalogModels.length === 0 || !operation) {
    return [];
  }
  return catalogModels.filter(
    (model) => model.capabilities?.imageEdit?.[operation]?.supported === true
  );
}
